// src/components/reports/ProjectReport.jsx
import React from 'react';
import { formatCurrency, formatDate } from '../../utils/formatters';
import {
  Chart as ChartJS, ArcElement, Tooltip, Legend,
  CategoryScale, LinearScale, BarElement, Title
} from 'chart.js';
import { Pie, Bar } from 'react-chartjs-2';

ChartJS.register(ArcElement, Tooltip, Legend, CategoryScale, LinearScale, BarElement, Title);

const CATEGORY_LABELS = {
  MATERIALS: 'Materiali', EQUIPMENT: 'Attrezzature', LABOR: 'Manodopera',
  FUEL: 'Carburante', SUBCONTRACT: 'Subappalti', TRANSPORT: 'Trasporti',
  PERMITS: 'Permessi', ADMIN: 'Amministrative', OTHER: 'Altro'
};

const STATUS_LABELS = {
  DRAFT: 'Bozza', PLANNED: 'Pianificato', IN_PROGRESS: 'In Corso',
  ON_HOLD: 'In Attesa', DELAYED: 'Ritardato', COMPLETED: 'Completato', CANCELLED: 'Cancellato'
};

const STATUS_COLORS = {
  DRAFT: 'bg-gray-100 text-gray-700', PLANNED: 'bg-blue-100 text-blue-700',
  IN_PROGRESS: 'bg-emerald-100 text-emerald-700', ON_HOLD: 'bg-yellow-100 text-yellow-700',
  DELAYED: 'bg-orange-100 text-orange-700', COMPLETED: 'bg-purple-100 text-purple-700',
  CANCELLED: 'bg-red-100 text-red-700'
};

const CHART_COLORS = ['#3B82F6','#10B981','#F59E0B','#EF4444','#8B5CF6','#EC4899','#06B6D4','#6B7280','#9CA3AF'];

const StatCard = ({ label, value, sub, color = 'blue' }) => (
  <div className={`bg-${color}-50 border border-${color}-100 rounded-2xl p-5`}>
    <p className={`text-xs font-semibold text-${color}-500 uppercase tracking-wider`}>{label}</p>
    <p className={`text-2xl font-bold text-${color}-700 mt-1`}>{value}</p>
    {sub && <p className={`text-xs text-${color}-400 mt-1`}>{sub}</p>}
  </div>
);

const ProjectReport = ({ data }) => {
  // ✅ Guard — previene crash se data o stats non sono ancora caricati
  if (!data || !data.project || !data.stats) {
    return (
      <div className="bg-white rounded-2xl shadow-sm p-8 text-center text-gray-400">
        <p>Nessun dato disponibile per il report</p>
      </div>
    );
  }

  const { project, stats } = data;
  const expensesByCategory = stats.expensesByCategory || [];
  const taskStatus = stats.taskStatus || [];
  const utilization = project.estimatedBudget > 0
    ? ((stats.totalExpenses / project.estimatedBudget) * 100).toFixed(1)
    : 0;

  const utilizationNum = parseFloat(utilization);
  const utilizationColor = utilizationNum >= 90 ? 'red' : utilizationNum >= 70 ? 'yellow' : 'emerald';
  const utilizationBarColor = utilizationNum >= 90 ? 'bg-red-500' : utilizationNum >= 70 ? 'bg-yellow-400' : 'bg-emerald-500';

  const pieData = {
    labels: expensesByCategory.map(c => CATEGORY_LABELS[c.category] || c.category),
    datasets: [{
      data: expensesByCategory.map(c => c._sum?.totalAmount || 0),
      backgroundColor: CHART_COLORS,
      borderWidth: 0,
    }]
  };

  const barData = {
    labels: taskStatus.map(t => ({ TODO: 'Da fare', IN_PROGRESS: 'In corso', BLOCKED: 'Bloccato', COMPLETED: 'Completato' }[t.status] || t.status)),
    datasets: [{
      label: 'Task',
      data: taskStatus.map(t => t._count || 0),
      backgroundColor: ['#94A3B8','#3B82F6','#EF4444','#10B981'],
      borderRadius: 6,
    }]
  };

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-600 to-indigo-600 rounded-2xl p-6 text-white">
        <div className="flex items-start justify-between">
          <div>
            <p className="text-blue-200 text-sm font-medium uppercase tracking-wider">Rapporto Progetto</p>
            <h2 className="text-2xl font-bold mt-1">{project.name}</h2>
            <p className="text-blue-200 text-sm mt-1">{project.code} · {project.location}</p>
          </div>
          <span className={`px-3 py-1.5 rounded-full text-xs font-semibold ${STATUS_COLORS[project.status]}`}>
            {STATUS_LABELS[project.status] || project.status}
          </span>
        </div>

        {/* Barra avanzamento budget */}
        <div className="mt-5">
          <div className="flex justify-between text-sm text-blue-200 mb-1.5">
            <span>Utilizzo Budget</span>
            <span className="font-bold text-white">{utilization}%</span>
          </div>
          <div className="h-2.5 bg-white/20 rounded-full overflow-hidden">
            <div
              className={`h-full ${utilizationBarColor} transition-all duration-700`}
              style={{ width: `${Math.min(utilizationNum, 100)}%` }}
            />
          </div>
        </div>
      </div>

      {/* Info progetto */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-white rounded-xl border p-4">
          <p className="text-xs text-gray-400 uppercase tracking-wider">Inizio</p>
          <p className="font-semibold text-gray-900 mt-1">{formatDate(project.startDate)}</p>
        </div>
        <div className="bg-white rounded-xl border p-4">
          <p className="text-xs text-gray-400 uppercase tracking-wider">Fine</p>
          <p className="font-semibold text-gray-900 mt-1">{project.endDate ? formatDate(project.endDate) : 'In corso'}</p>
        </div>
        <div className="bg-white rounded-xl border p-4">
          <p className="text-xs text-gray-400 uppercase tracking-wider">Cliente</p>
          <p className="font-semibold text-gray-900 mt-1 truncate">{project.clientName || '—'}</p>
        </div>
        <div className="bg-white rounded-xl border p-4">
          <p className="text-xs text-gray-400 uppercase tracking-wider">Progresso</p>
          <p className="font-semibold text-gray-900 mt-1">{project.progress || 0}%</p>
        </div>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <StatCard label="Totale Spese" value={formatCurrency(stats.totalExpenses)} sub={`${stats.expenseCount || 0} transazioni`} color="blue" />
        <StatCard label="Task Totali" value={stats.totalTasks || 0} sub={`Completati: ${taskStatus.find(t => t.status === 'COMPLETED')?._count || 0}`} color="emerald" />
        <StatCard label="Budget Stimato" value={formatCurrency(project.estimatedBudget)} sub={`Contratto: ${formatCurrency(project.contractAmount)}`} color="purple" />
      </div>

      {/* Grafici */}
      {expensesByCategory.length > 0 && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="bg-white rounded-2xl border p-5">
            <h3 className="font-semibold text-gray-900 mb-4">Spese per Categoria</h3>
            <div className="h-60">
              <Pie data={pieData} options={{ maintainAspectRatio: false, plugins: { legend: { position: 'bottom', labels: { boxWidth: 12, padding: 16, font: { size: 11 } } } } }} />
            </div>
          </div>
          {taskStatus.length > 0 && (
            <div className="bg-white rounded-2xl border p-5">
              <h3 className="font-semibold text-gray-900 mb-4">Task per Stato</h3>
              <div className="h-60">
                <Bar data={barData} options={{
                  maintainAspectRatio: false,
                  plugins: { legend: { display: false } },
                  scales: { y: { beginAtZero: true, ticks: { stepSize: 1 }, grid: { color: '#F1F5F9' } }, x: { grid: { display: false } } }
                }} />
              </div>
            </div>
          )}
        </div>
      )}

      {/* Dettaglio spese */}
      {project.expenses && project.expenses.length > 0 && (
        <div className="bg-white rounded-2xl border overflow-hidden">
          <div className="px-6 py-4 border-b bg-gray-50">
            <h3 className="font-semibold text-gray-900">Dettaglio Spese</h3>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-100">
              <thead>
                <tr className="text-xs text-gray-400 uppercase tracking-wider">
                  <th className="px-6 py-3 text-left">Riferimento</th>
                  <th className="px-6 py-3 text-left">Descrizione</th>
                  <th className="px-6 py-3 text-left">Categoria</th>
                  <th className="px-6 py-3 text-right">Importo</th>
                  <th className="px-6 py-3 text-left">Data</th>
                  <th className="px-6 py-3 text-center">Stato</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {project.expenses.slice(0, 10).map((expense) => (
                  <tr key={expense.id} className="hover:bg-gray-50 transition-colors">
                    <td className="px-6 py-3 text-sm font-mono text-gray-700">{expense.reference}</td>
                    <td className="px-6 py-3 text-sm text-gray-600 max-w-xs truncate">{expense.description}</td>
                    <td className="px-6 py-3 text-sm text-gray-500">{CATEGORY_LABELS[expense.category] || expense.category}</td>
                    <td className="px-6 py-3 text-sm font-semibold text-gray-900 text-right">{formatCurrency(expense.totalAmount)}</td>
                    <td className="px-6 py-3 text-sm text-gray-500">{formatDate(expense.invoiceDate)}</td>
                    <td className="px-6 py-3 text-center">
                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${expense.validated ? 'bg-emerald-100 text-emerald-700' : 'bg-yellow-100 text-yellow-700'}`}>
                        {expense.validated ? '✓ Validato' : '⏳ In attesa'}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {project.expenses.length > 10 && (
              <p className="text-center text-sm text-gray-400 py-3">
                + altre {project.expenses.length - 10} spese non mostrate
              </p>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default ProjectReport;